import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/css';
import 'swiper/css/free-mode';
import 'swiper/css/pagination';
import SwiperCore from 'swiper';
import { FreeMode, Autoplay, Navigation } from 'swiper/modules';

SwiperCore.use([FreeMode, Autoplay, Navigation]);

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://medical-linkers.onrender.com';

const FeaturedTestimonialVideoCarousel = ({ 
  title = "Featured Video Testimonials", 
  subtitle = "Highlighted success stories from our patients",
  limit = 8, 
  className = ""
}) => {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const swiperRef = useRef(null);

  useEffect(() => {
    fetchFeaturedVideos();
  }, [limit]);

  const fetchFeaturedVideos = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const params = new URLSearchParams({
        limit: limit.toString(),
        status: 'published',
        featured: 'true' // Only fetch featured videos
      });

      const response = await fetch(`${API_BASE_URL}/api/testimonial-videos?${params}`);
      
      if (!response.ok) {
        throw new Error('Failed to fetch featured testimonial videos');
      }
      
      const data = await response.json();
      setVideos(data.videos || []);
    } catch (error) {
      console.error('Error fetching featured testimonial videos:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getYouTubeEmbedUrl = (youtubeUrl) => {
    const videoIdMatch = youtubeUrl.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\n?#]+)/);
    if (videoIdMatch) {
      return `https://www.youtube.com/embed/${videoIdMatch[1]}`;
    }
    return youtubeUrl;
  };

  const openYouTubeInNewTab = (youtubeUrl) => {
    window.open(youtubeUrl, '_blank', 'noopener,noreferrer');
  };

  const playVideo = (video) => {
    setSelectedVideo(video);
  };

  const closeVideo = () => {
    setSelectedVideo(null);
  };

  if (loading) {
    return (
      <section className={`container mx-auto px-4 py-16 ${className}`}>
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold">
            <span className="text-[#1976f2]">{title}</span>
          </h2>
          {subtitle && (
            <p className="text-gray-600 mt-4 max-w-2xl mx-auto">{subtitle}</p>
          )}
        </div>
        <div className="flex justify-center items-center min-h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1976f2]"></div>
        </div>
      </section>
    );
  }

  if (error || videos.length === 0) {
    return null; // Don't show the section if no featured videos are available
  }

  return (
    <section className={`container mx-auto px-4 py-16 ${className}`}>
      <div className="text-center mb-12">
        <h2 className="text-4xl font-bold">
          <span className="text-[#1976f2]">{title}</span>
        </h2>
        {subtitle && (
          <p className="text-gray-600 mt-4 max-w-2xl mx-auto">{subtitle}</p>
        )}
      </div>

      <div className="overflow-hidden relative">
        <Swiper
          ref={swiperRef}
          slidesPerView={1}
          spaceBetween={24}
          loop={videos.length > 1}
          freeMode={true}
          autoplay={{ delay: 5000, disableOnInteraction: false }}
          speed={1000}
          grabCursor={true}
          navigation={{
            nextEl: '.featured-videos-swiper-button-next',
            prevEl: '.featured-videos-swiper-button-prev',
          }}
          breakpoints={{
            640: {
              slidesPerView: 2,
            },
            768: {
              slidesPerView: 2,
            },
            1024: {
              slidesPerView: 3,
            },
          }}
          className="w-full"
        >
          {videos.map((video, index) => (
            <SwiperSlide key={`${video._id}-${index}`}>
              <div className="transition-transform duration-300 ease-in-out transform hover:scale-105">
                <div className="bg-white rounded-lg overflow-hidden shadow-md hover:shadow-xl transition-shadow h-full relative">
                  {/* Featured Badge */}
                  <div className="absolute top-2 left-2 z-10">
                    <span className="bg-gradient-to-r from-yellow-400 to-yellow-600 text-white px-3 py-1 rounded-full text-xs font-bold flex items-center shadow-lg">
                      <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/>
                      </svg>
                      Featured
                    </span>
                  </div>

                  {/* Video Thumbnail with Play Button */}
                  <div className="relative aspect-video overflow-hidden group">
                    <img 
                      src={video.thumbnailUrl} 
                      alt={video.title}
                      className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                      onError={(e) => {
                        e.target.src = "https://dummyimage.com/600x400/eeeeee/999999?text=Video+Thumbnail";
                      }}
                    />
                    {/* Play Button Overlay */}
                    <div className="absolute inset-0 bg-black bg-opacity-30 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <button
                        onClick={() => playVideo(video)}
                        className="w-12 h-12 bg-[#1976f2] rounded-full flex items-center justify-center text-white hover:bg-blue-700 transition-colors shadow-lg"
                        aria-label="Play video"
                      >
                        <svg className="w-5 h-5 ml-1" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" />
                        </svg>
                      </button>
                    </div>
                  </div>
                  
                  <div className="p-6">
                    <div className="mb-3 flex items-center justify-between text-sm text-gray-500">
                      <span className="bg-[#1976f2] text-white px-2 py-1 rounded text-xs">
                        {video.category}
                      </span>
                      <span>{formatDate(video.publishedAt || video.createdAt)}</span>
                    </div>
                    <h3 className="text-lg font-semibold text-gray-800 mb-3 leading-tight hover:text-[#1976f2] transition-colors">
                      {video.title}
                    </h3>
                    {video.description && (
                      <p className="text-gray-600 text-sm mb-4 line-clamp-2">
                        {video.description}
                      </p>
                    )}
                    <div className="flex items-center justify-between">
                      <button
                        onClick={() => openYouTubeInNewTab(video.youtubeUrl)}
                        className="text-[#1976f2] hover:text-blue-700 font-medium inline-flex items-center transition-colors"
                      >
                        Watch Now <span className="ml-1">→</span>
                      </button>
                      {video.duration && (
                        <span className="text-xs text-gray-400">
                          {video.duration}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </SwiperSlide>
          ))}
        </Swiper>

        {/* Custom Navigation Buttons */}
        <div className="flex justify-center gap-4 mt-10">
          <button
            className="featured-videos-swiper-button-prev w-12 h-12 rounded-full flex items-center justify-center bg-[#1976f2] hover:bg-blue-700 text-white transition-colors"
            aria-label="Previous"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <button
            className="featured-videos-swiper-button-next w-12 h-12 rounded-full flex items-center justify-center bg-[#1976f2] hover:bg-blue-700 text-white transition-colors"
            aria-label="Next"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>
      </div>

      {/* View All Button */}
      <div className="text-center mt-8">
        <Link 
          to="/testimonial-videos"
          onClick={() => window.scrollTo(0, 0)}
          className="bg-[#1976f2] text-white px-8 py-3 rounded-full font-semibold hover:bg-[#125ea2] transition-colors inline-block"
        >
          View All Videos
        </Link>
      </div>

      {/* Video Modal */}
      {selectedVideo && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-75">
          <div className="relative bg-white rounded-lg overflow-hidden max-w-4xl mx-4 w-full">
            <button
              onClick={closeVideo}
              className="absolute top-4 right-4 text-white bg-black bg-opacity-50 rounded-full w-8 h-8 flex items-center justify-center z-10 hover:bg-opacity-70"
            >
              ×
            </button>
            <div className="aspect-video">
              <iframe
                width="100%"
                height="100%"
                src={`${getYouTubeEmbedUrl(selectedVideo.youtubeUrl)}?autoplay=1`}
                title={selectedVideo.title}
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              ></iframe>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default FeaturedTestimonialVideoCarousel;
